import React, { useId, useRef, useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Camera, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { db, storage } from '@/lib/firebase';
import { UserAvatar } from '@/components/shared/UserAvatar';

const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const AVATAR_TYPES = new Map<string, string>([
  ['image/jpeg', 'jpg'],
  ['image/png', 'png'],
  ['image/gif', 'gif'],
  ['image/webp', 'webp'],
]);

interface AvatarUploadControlProps {
  currentAvatarUrl?: string | null;
  displayName?: string | null;
  email?: string | null;
  buttonLabel?: string;
  helperText?: string;
  showPreview?: boolean;
  avatarClassName?: string;
  buttonVariant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  onUploaded?: (avatarUrl: string) => void | Promise<void>;
}

export const AvatarUploadControl: React.FC<AvatarUploadControlProps> = ({
  currentAvatarUrl,
  displayName,
  email,
  buttonLabel = 'Change Avatar',
  helperText = 'JPG, PNG, GIF or WebP. Max size 2MB.',
  showPreview = true,
  avatarClassName = 'h-20 w-20 text-2xl',
  buttonVariant = 'outline',
  buttonSize = 'sm',
  onUploaded,
}) => {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { user, refreshProfile } = useAuth();
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';

    if (!file || !user) return;

    const extension = AVATAR_TYPES.get(file.type);
    if (!extension) {
      toast.error('Please upload a JPG, PNG, GIF, or WebP avatar.');
      return;
    }

    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('Avatar must be 2MB or smaller.');
      return;
    }

    setUploading(true);
    try {
      const avatarRef = ref(storage, `users/${user.uid}/profile/avatar.${extension}`);
      await uploadBytes(avatarRef, file, {
        contentType: file.type,
        customMetadata: {
          owner: user.uid,
          purpose: 'profile-avatar',
        },
      });

      const downloadUrl = await getDownloadURL(avatarRef);
      const cacheSafeUrl = `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;

      await setDoc(doc(db, 'users', user.uid), {
        photoURL: cacheSafeUrl,
        avatarUrl: cacheSafeUrl,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await updateProfile(user, { photoURL: cacheSafeUrl });
      await refreshProfile();
      await onUploaded?.(cacheSafeUrl);

      toast.success('Avatar updated');
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      toast.error('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      {showPreview && (
        <div className="relative">
          <UserAvatar
            src={currentAvatarUrl}
            name={displayName}
            email={email}
            className={avatarClassName}
          />
          <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground shadow-sm">
            <Sparkles className="h-3.5 w-3.5" />
          </div>
        </div>
      )}

      <div>
        <input
          id={inputId}
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="sr-only"
          onChange={handleFileChange}
          aria-label="Upload avatar image"
        />
        <Button
          type="button"
          variant={buttonVariant}
          size={buttonSize}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Camera className="h-4 w-4 mr-2" />
          )}
          {uploading ? 'Uploading...' : buttonLabel}
        </Button>
        {helperText && (
          <p className="text-xs text-muted-foreground mt-2">
            {helperText}
          </p>
        )}
      </div>
    </div>
  );
};
